// FO-318 (batch-35) — de verloning-telregels als pure engine.
//
// Dit is HET geld-pad. `berekenPeriodeAfrekening(input)` legt de normatieve
// telregels van FO-318 §2 vast in één pure, zero-dep, volledig unit-geteste
// functie. De API-aggregatie (`aggregatePayrollMonth`) wordt een dunne data-
// verzamelaar die deze engine voedt met per-dag werkuren + de al-geresolvede
// toeslag-uren + de maand-contracturen, en de reservering-categorieën terugkrijgt.
//
// SUPERSEDE (ADDENDA batch-35): FO-318 vervangt de FO-305-overuren-indeling
// (100/125/150/200) door de DAG-staffel >8=25% / >10=50% / >12=100% op de
// werkuren-na-pauze per KALENDERDAG (som over alle bonnen die dag). De
// FO-012-periode-saldering (tekort → reisuren-aanvulling → contracturen betaald;
// overschot → reservering) blijft de §2.2/§2.4-kern en wordt hier hergebruikt
// (`berekenPeriodeSaldering`) i.p.v. gedupliceerd.
//
// Conventies (CLAUDE.md): geld/uren pas op de rand afronden (2 decimalen); geen
// date-fns/dayjs; datums als `yyyy-mm-dd` NL-kalenderdag (de caller splitst
// over-middernacht-diensten al per dag via `splitDayEntryByDay`, FO-104).
// Correcties/categorieën nooit onder 0 (§7).

import {
  berekenPeriodeSaldering,
  type PeriodeSalderingResultaat,
  type PeriodeSalderingStatus,
} from './payroll-periode-saldering'

export type { PeriodeSalderingStatus } from './payroll-periode-saldering'

/** Afronding op urenniveau (2 decimalen); niet-eindige invoer → 0. */
function round2(n: number): number {
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100) / 100
}

/** Zet een niet-eindige of negatieve waarde veilig op 0 (en rond af). */
function clampNonNeg(n: number): number {
  return Number.isFinite(n) && n > 0 ? round2(n) : 0
}

// ────────────────────────────────────────────────────────────────────────────
// DAG-staffel (FO-318 §2.3) — premie per kalenderdag op de werkuren-na-pauze.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Eén band in de DAG-staffel. `totUur` = cumulatieve bovengrens in uren
 * (10 = "t/m uur 10"); `null` = laatste band (oneindig). `premie` = de
 * toeslag-FRACTIE boven op het 100%-basisuur (0.25 = 25%). De grondslag-uren
 * zelf tellen in §2.2 mee; deze staffel betreft uitsluitend de premie erbovenop.
 */
export interface DagStaffelBand {
  totUur: number | null
  premie: number
  label: string
}

/**
 * FO-318 §2.3 DAG-staffel (default): > 8 en ≤ 10 → 25%; > 10 en ≤ 12 → 50%;
 * > 12 → 100%. Drempels 8/10/12 centraal zodat een rooster-uitzondering
 * (CC.0b: instelbaar per rooster, default uit) een andere staffel kan meegeven.
 * VERVANGT de FO-305 100/125/150/200-factor-indeling als verloning-regel.
 */
export const DEFAULT_DAG_STAFFEL: readonly DagStaffelBand[] = [
  { totUur: 8, premie: 0, label: '0%' },
  { totUur: 10, premie: 0.25, label: '25%' },
  { totUur: 12, premie: 0.5, label: '50%' },
  { totUur: null, premie: 1.0, label: '100%' },
]

/** Uren per premie-band voor één dag (geen geld). Som = de premie-uren > 8u. */
export interface DagStaffelBandUren {
  premie: number
  label: string
  uren: number
}

/**
 * Splits de werkuren van ÉÉN kalenderdag (na pauze-aftrek) in de staffel-banden.
 * Alleen de banden mét een premie (> basis) worden teruggegeven; de basisband
 * (0%) telt niet als toeslag. De som van de teruggegeven band-uren is exact het
 * aantal uren boven de eerste drempel. Lege array bij ≤ 0 of ≤ 8 uur.
 */
export function splitDagUrenInStaffelBanden(
  werkuren: number,
  staffel: readonly DagStaffelBand[] = DEFAULT_DAG_STAFFEL,
): DagStaffelBandUren[] {
  const out: DagStaffelBandUren[] = []
  if (!Number.isFinite(werkuren) || werkuren <= 0) return out
  let ondergrens = 0
  for (const band of staffel) {
    const bovengrens = band.totUur ?? Number.POSITIVE_INFINITY
    const urenInBand = Math.min(werkuren, bovengrens) - ondergrens
    if (urenInBand > 0 && band.premie > 0) {
      out.push({ premie: band.premie, label: band.label, uren: urenInBand })
    }
    if (werkuren <= bovengrens) break
    ondergrens = bovengrens
  }
  return out
}

/** DAG-staffel uitgesplitst naar de drie FO-318-premie-categorieën (uren). */
export interface DagStaffelUitsplitsing {
  /** Uren met 25%-premie (> 8 en ≤ 10 per dag). */
  toeslag25: number
  /** Uren met 50%-premie (> 10 en ≤ 12 per dag). */
  toeslag50: number
  /** Uren met 100%-premie (> 12 per dag). */
  toeslag100: number
}

/**
 * Accumuleer de DAG-staffel over alle kalenderdagen van de periode. Cruciaal:
 * de staffel wordt PER KALENDERDAG toegepast op de som van de werkuren die dag
 * (meerdere bonnen samen), NIET op het periode-totaal — anders zou een dag van
 * 4u + een dag van 9u samen als één 13-uurs dag tellen.
 *
 * `premie`-matching op waarde (0.25/0.5/1.0) zodat een aangepaste staffel met
 * dezelfde premies blijft mappen; premies buiten die drie tellen (defensief) niet
 * mee in de categorieën maar gaan ook niet verloren voor de test (som-invariant).
 */
export function berekenDagStaffel(
  dagen: ReadonlyArray<{ werkuren: number }>,
  staffel: readonly DagStaffelBand[] = DEFAULT_DAG_STAFFEL,
): DagStaffelUitsplitsing {
  let t25 = 0
  let t50 = 0
  let t100 = 0
  for (const dag of dagen) {
    for (const band of splitDagUrenInStaffelBanden(dag.werkuren, staffel)) {
      if (band.premie === 0.25) t25 += band.uren
      else if (band.premie === 0.5) t50 += band.uren
      else if (band.premie === 1.0) t100 += band.uren
    }
  }
  return { toeslag25: round2(t25), toeslag50: round2(t50), toeslag100: round2(t100) }
}

// ────────────────────────────────────────────────────────────────────────────
// De periode-afrekening (FO-318 §2 in z'n geheel).
// ────────────────────────────────────────────────────────────────────────────

/** Eén kalenderdag met de gewerkte uren (na pauze), som over alle bonnen die dag. */
export interface AfrekenDag {
  /** `yyyy-mm-dd` NL-kalenderdag (informatief / groepeersleutel voor de caller). */
  datum: string
  /** Werkuren na pauze-aftrek (§2.3: pauze is al afgetrokken vóór de staffel). */
  werkuren: number
}

/**
 * Toeslag-UREN per ploegen-/weekendcategorie, ZOALS de configureerbare toeslag-
 * engine (`resolveToeslagen` + `bucketToeslagUren`) ze al heeft geresolved en
 * gerouteerd. De 5-ploegen-weekend-regel (§2.5: geen aparte weekendtoeslag bij
 * 5-ploegen) zit al in die routering — za/zo binnen een 5-ploegendienst telt in
 * `vijfPloegen`, niet in `zaterdag`/`zondag`. De engine reserveert deze uren 1:1.
 */
export interface AfrekenToeslagUren {
  tweePloegen: number
  driePloegen: number
  vijfPloegen: number
  /** Incidenteel zaterdagwerk BUITEN een 5-ploegenrooster (§2.5: za 50%). */
  zaterdag: number
  /** Incidenteel zondagwerk BUITEN een 5-ploegenrooster (§2.5: zo 100%). */
  zondag: number
}

export interface PeriodeAfrekeningInput {
  /**
   * Werkuren per KALENDERDAG (na pauze), incl. cursus-/interne uren (§3.3: die
   * tellen als werkuren). Meerdere bonnen op één dag zijn al gesommeerd; over-
   * middernacht-diensten al per dag gesplitst (FO-104). Bron voor het periode-
   * totaal én de DAG-staffel.
   */
  dagen: ReadonlyArray<AfrekenDag>
  /**
   * Contracturen-maand-norm (uit de gedeelde x52/12-helper). 0 = oproep-/nul-
   * urencontract → geen tekort-/overschot-norm (§7: alle werkuren uitbetaald).
   */
  contractUren: number
  /**
   * Doorbetaalde afwezigheid (verlof + ziekte + feestdag) die de te-halen norm
   * VERLAAGT (§2.2, geen dubbeltelling). Excl. cursus/opleiding (die tellen al
   * als werkuren in `dagen`).
   */
  doorbetaaldeAfwezigheidUren: number
  /** Totaal uitbetaalde reisuren in de periode (heen + terug). */
  reisUren: number
  /** Al-geresolvede toeslag-uren per categorie (§2.5). */
  toeslagUren: AfrekenToeslagUren
  /**
   * Optionele staffel-override (CC.0b: instelbare rooster-uitzondering, default
   * uit). Default = `DEFAULT_DAG_STAFFEL` (25/50/100 op 8/10/12).
   */
  staffel?: readonly DagStaffelBand[]
}

/** De te reserveren categorieën (§4) — betaling in de volgende periode. */
export interface AfrekenReservering {
  /** §2.2.3 — 100%-overschot boven de norm (period-net). */
  overschotUren: number
  /** §2.3 — DAG-staffel premie-uren > 8 en ≤ 10 (25%). */
  overurenToeslag25: number
  /** §2.3 — DAG-staffel premie-uren > 10 en ≤ 12 (50%). */
  overurenToeslag50: number
  /** §2.3 — DAG-staffel premie-uren > 12 (100%). */
  overurenToeslag100: number
  /** §2.4 — reisuren die ná de tekort-aanvulling resteren. */
  reisUren: number
  /** §2.5 — ploegentoeslag-uren 2-ploegen. */
  ploegtoeslag2: number
  /** §2.5 — ploegentoeslag-uren 3-ploegen. */
  ploegtoeslag3: number
  /** §2.5 — ploegentoeslag-uren 5-ploegen (incl. het rooster-weekend). */
  ploegtoeslag5: number
  /** §2.5 — zaterdagtoeslag-uren (buiten 5-ploegen). */
  zaterdag: number
  /** §2.5 — zondagtoeslag-uren (buiten 5-ploegen). */
  zondag: number
}

export interface PeriodeAfrekeningResultaat {
  /** Totaal werkuren in de periode (som van `dagen`, incl. cursus/intern). */
  werkuren: number
  /** Verlaagde norm = max(0, contractUren − doorbetaalde afwezigheid). */
  effectieveNorm: number
  /** werkuren − effectieveNorm (signed; basis voor de status). */
  saldoUren: number
  status: PeriodeSalderingStatus
  /**
   * De 100%-uren die deze periode als BASIS worden uitbetaald: bij een contract
   * = de effectieve norm (het vaste salaris-deel voor gewerkte uren); bij oproep/
   * nul-uren = alle werkuren (§7). Het overschot (contract) resp. de reservering
   * loopt apart via `reservering`.
   */
  uitbetaaldeUren: number
  /**
   * §2.2.1/§2.4 — reisuren ingezet om een tekort tot de contracturen aan te
   * vullen (100%, NIET gereserveerd — geen dubbeltelling).
   */
  reisAanvulling: number
  /**
   * §2.2.2 — het tekort dat ná reisuren-aanvulling resteert. Het vaste
   * contractsalaris blijft (geen looningreep); dit is het min-uren-signaal.
   */
  restTekortUren: number
  reservering: AfrekenReservering
}

/**
 * Bereken de periode-afrekening voor één medewerker over één kalendermaand,
 * exact conform FO-318 §2. Pure functie; SLUITEND (geen uur verdwijnt of telt
 * dubbel).
 *
 * Stappen:
 *   1. werkuren = Σ dagen.werkuren (incl. cursus/intern, §3.3).
 *   2. §2.2/§2.4 periode-saldering (`berekenPeriodeSaldering`): tekort → aanvullen
 *      met reisuren (100%, niet gereserveerd) → resterend tekort = min-uren
 *      (contractsalaris blijft); overschot → reserveren; resterende reisuren →
 *      reserveren.
 *   3. §2.3 DAG-staffel per kalenderdag → premie-uren 25/50/100 → reserveren.
 *   4. §2.5 toeslag-uren (al 5-ploegen-weekend-gerouteerd) → reserveren.
 *   5. Oproep/nul-uren (contractUren ≤ 0): geen tekort-/overschot-mechanisme; alle
 *      werkuren uitbetaald (§7), reisuren volledig gereserveerd. Staffel + toeslag
 *      gelden onverkort.
 *
 * TVT: uren die per contract als TVT worden opgebouwd volgen de bestaande
 * contract-gate (premie-only) UPSTREAM en zitten niet dubbel in deze reservering
 * — de engine reserveert elke categorie precies één keer.
 */
export function berekenPeriodeAfrekening(
  input: PeriodeAfrekeningInput,
): PeriodeAfrekeningResultaat {
  const staffel = input.staffel ?? DEFAULT_DAG_STAFFEL
  const werkuren = round2(
    input.dagen.reduce((som, d) => som + (Number.isFinite(d.werkuren) ? d.werkuren : 0), 0),
  )
  const contractUren = clampNonNeg(input.contractUren)
  const reisUren = clampNonNeg(input.reisUren)
  const dagStaffel = berekenDagStaffel(input.dagen, staffel)

  const ploegtoeslag2 = clampNonNeg(input.toeslagUren.tweePloegen)
  const ploegtoeslag3 = clampNonNeg(input.toeslagUren.driePloegen)
  const ploegtoeslag5 = clampNonNeg(input.toeslagUren.vijfPloegen)
  const zaterdag = clampNonNeg(input.toeslagUren.zaterdag)
  const zondag = clampNonNeg(input.toeslagUren.zondag)

  // §7 — oproep/nul-uren (contractUren = 0) heeft geen apart pad nodig: de
  // saldering geeft dan effectieveNorm = 0 → status OVERSCHOT → overschotUren =
  // werkuren (alle werkuren "boven de norm 0"), en reisUren volledig over. De
  // export routeert dat overschot naar de oproepkracht-looncode (U2100) i.p.v.
  // extra-uren (U2190) op basis van de contractvorm (FO-319). Geen tekort mogelijk
  // (norm 0), dus "geen tekort-mechanisme" is vanzelf gedekt.
  const saldering: PeriodeSalderingResultaat = berekenPeriodeSaldering({
    honderdProcentUren: werkuren,
    contractUrenPeriode: contractUren,
    doorbetaaldeAfwezigheidUren: input.doorbetaaldeAfwezigheidUren,
    reisUren,
  })

  return {
    werkuren,
    effectieveNorm: saldering.effectieveNorm,
    saldoUren: saldering.saldoUren,
    status: saldering.status,
    // §2.2.2 — bij tekort wordt gewoon op contracturen (de effectieve norm)
    // verloond; bij overschot idem (het meerdere gaat naar de reservering).
    uitbetaaldeUren: saldering.effectieveNorm,
    reisAanvulling: saldering.verrekendeReisUren,
    restTekortUren: saldering.restTekortUren,
    reservering: {
      overschotUren: saldering.overschotUren,
      overurenToeslag25: dagStaffel.toeslag25,
      overurenToeslag50: dagStaffel.toeslag50,
      overurenToeslag100: dagStaffel.toeslag100,
      reisUren: saldering.overigeReisUren,
      ploegtoeslag2,
      ploegtoeslag3,
      ploegtoeslag5,
      zaterdag,
      zondag,
    },
  }
}
